import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { TimezonePicker } from '../components/TimezonePicker';
import toast from 'react-hot-toast';

interface TenantFormData {
  name: string;
  expiresAt: string;
  timezone: string;
  adminUser: {
    nome: string;
    email: string;
    senha: string;
  };
  quotas: {
    maxUsers: string;
    maxContacts: string;
    maxCampaigns: string;
    maxConnections: string;
    maxMessagesPerMonth: string;
    billingCycleDay: string;
  };
  allowedProviders: string[];
}

interface Tenant {
  id: string;
  slug: string;
  name: string;
  domain?: string;
  active: boolean;
  expiresAt?: string | null;
  allowedProviders?: string[];
  timezone?: string;
  createdAt: string;
  updatedAt: string;
  users?: Array<{ nome: string; email: string; role: string }>;
  quota?: {
    maxUsers: number;
    maxContacts: number;
    maxCampaigns: number;
    maxConnections: number;
    maxMessagesPerMonth: number;
    messagesSentThisMonth: number;
    billingCycleDay: number;
  };
  quotas?: Tenant['quota'];
}

const PROVIDER_INFO = {
  WAHA: {
    name: 'WAHA',
    description: 'WhatsApp HTTP API',
    logo: '/iconewaha.png'
  },
  EVOLUTION: {
    name: 'Evolution API',
    description: 'Evolution WhatsApp API',
    logo: '/iconeevolutionapi.png'
  },
  QUEPASA: {
    name: 'Quepasa',
    description: 'Quepasa WhatsApp API',
    logo: '/iconequepasa.png'
  }
};

export function CreateCompanyPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'novo';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    expiresAt: '',
    timezone: 'America/Sao_Paulo',
    adminUser: {
      nome: '',
      email: '',
      senha: ''
    },
    quotas: {
      maxUsers: '',
      maxContacts: '',
      maxCampaigns: '',
      maxConnections: '',
      maxMessagesPerMonth: '',
      billingCycleDay: ''
    },
    allowedProviders: ['WAHA', 'EVOLUTION', 'QUEPASA']
  });

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      fetch(`/api/tenants/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            const tenant: Tenant = data.tenant ?? data;

            const quota = tenant.quotas ?? tenant.quota;

            let expiresAtFormatted = '';
            let billingCycleDayFromExpires = '1';

            if (tenant.expiresAt) {
              const date = new Date(tenant.expiresAt);
              expiresAtFormatted = date.toISOString().split('T')[0];
              const day = date.getDate();
              billingCycleDayFromExpires = Math.min(28, Math.max(1, day)).toString();
            }

            const finalBillingCycleDay = tenant.expiresAt
              ? billingCycleDayFromExpires
              : (quota?.billingCycleDay?.toString() ?? '1');

            const adminUser = tenant.users?.find((u) =>
              ['ADMIN', 'TENANT_ADMIN'].includes(u.role)
            );

            setFormData({
              name: tenant.name ?? '',
              expiresAt: expiresAtFormatted,
              timezone: tenant.timezone ?? 'America/Sao_Paulo',
              adminUser: {
                nome: adminUser?.nome ?? '',
                email: adminUser?.email ?? '',
                senha: ''
              },
              quotas: {
                maxUsers: quota?.maxUsers?.toString() ?? '',
                maxContacts: quota?.maxContacts?.toString() ?? '',
                maxCampaigns: quota?.maxCampaigns?.toString() ?? '',
                maxConnections: quota?.maxConnections?.toString() ?? '',
                maxMessagesPerMonth: quota?.maxMessagesPerMonth?.toString() ?? '',
                billingCycleDay: finalBillingCycleDay
              },
              allowedProviders: tenant.allowedProviders ?? ['WAHA', 'EVOLUTION', 'QUEPASA']
            });
          }
        })
        .finally(() => setLoading(false));
    }
  }, [isNew, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || (!isNew ? false : (!formData.adminUser.nome || !formData.adminUser.email || !formData.adminUser.senha))) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = isNew ? '/api/tenants' : `/api/tenants/${id}`;
      const method = isNew ? 'POST' : 'PUT';

      const q = formData.quotas;
      const parseNum = (v: string, defaultVal: number) => (v === '' ? defaultVal : parseInt(v, 10) || 0);
      const body: any = {
        name: formData.name,
        quotas: {
          maxUsers: parseNum(q.maxUsers, 0),
          maxContacts: parseNum(q.maxContacts, 0),
          maxCampaigns: parseNum(q.maxCampaigns, 0),
          maxConnections: parseNum(q.maxConnections, 0),
          maxMessagesPerMonth: parseNum(q.maxMessagesPerMonth, 0),
          billingCycleDay: parseNum(q.billingCycleDay, 1)
        },
        allowedProviders: formData.allowedProviders,
        timezone: formData.timezone || 'America/Sao_Paulo',
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null
      };

      if (isNew) {
        body.adminUser = formData.adminUser;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(`Empresa ${isNew ? 'criada' : 'atualizada'} com sucesso!`);
        navigate('/superadmin/empresas');
      } else {
        const error = await response.json();
        throw new Error(error.message || `Erro ao ${isNew ? 'criar' : 'atualizar'} empresa`);
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleProviderToggle = (provider: string) => {
    setFormData(prev => ({
      ...prev,
      allowedProviders: prev.allowedProviders.includes(provider)
        ? prev.allowedProviders.filter(p => p !== provider)
        : [...prev.allowedProviders, provider]
    }));
  };

  if (!isNew && loading) {
    return (
      <>
        <Header title="Empresas" subtitle="Carregando..." compactTopbar />
        <PageLayout>
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Header title="Empresas" subtitle={isNew ? 'Nova empresa' : 'Editar empresa'} compactTopbar />
      <PageLayout>
        <PageHeader
          title={isNew ? 'Nova empresa' : 'Editar empresa'}
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'SuperAdmin', href: '/superadmin/dashboard' },
            { label: 'Empresas', href: '/superadmin/empresas' },
            { label: isNew ? 'Nova' : 'Editar' },
          ]}
        />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">
                {isNew ? 'Nova empresa' : 'Editar empresa'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Layout em 2 colunas */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* COLUNA A: Informações da empresa e usuário */}
                <div className="space-y-6">
                  {/* Informações básicas */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Nome da empresa *
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Empresa ABC"
                      className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Fuso horário */}
                  <div>
                    <TimezonePicker
                      value={formData.timezone}
                      onChange={(tz) => setFormData({ ...formData, timezone: tz })}
                      disabled={saving}
                    />
                  </div>

                  {/* Usuário administrador - mesma seção em nova e edição */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Usuário administrador</h3>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="adminNome" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Nome {isNew && '*'}
                        </label>
                        <input
                          id="adminNome"
                          type="text"
                          value={formData.adminUser.nome}
                          onChange={(e) => setFormData({
                            ...formData,
                            adminUser: { ...formData.adminUser, nome: e.target.value }
                          })}
                          placeholder="Nome do administrador"
                          className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="adminEmail" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Email {isNew && '*'}
                        </label>
                        <input
                          id="adminEmail"
                          type="email"
                          value={formData.adminUser.email}
                          onChange={(e) => setFormData({
                            ...formData,
                            adminUser: { ...formData.adminUser, email: e.target.value }
                          })}
                          placeholder="email@empresa.com"
                          className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="adminSenha" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Senha {isNew && '*'}
                        </label>
                        <input
                          id="adminSenha"
                          type="password"
                          value={formData.adminUser.senha}
                          onChange={(e) => setFormData({
                            ...formData,
                            adminUser: { ...formData.adminUser, senha: e.target.value }
                          })}
                          placeholder={isNew ? 'Senha do administrador' : 'Deixe em branco para não alterar'}
                          className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* COLUNA B: Data de vencimento e Quotas */}
                <div className="space-y-6">
                  <div>
                    <label htmlFor="expiresAt" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Data de vencimento
                    </label>
                    <input
                      id="expiresAt"
                      type="date"
                      value={formData.expiresAt}
                      onChange={(e) => {
                        const value = e.target.value;
                        let newBillingCycleDay = formData.quotas.billingCycleDay;
                        if (value) {
                          const day = new Date(value).getDate();
                          newBillingCycleDay = Math.min(28, Math.max(1, day)).toString();
                        }
                        setFormData({
                          ...formData,
                          expiresAt: value,
                          quotas: { ...formData.quotas, billingCycleDay: newBillingCycleDay }
                        });
                      }}
                      className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Quotas */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Limites e quotas</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="maxUsers" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Usuários
                        </label>
                        <input
                          id="maxUsers"
                          type="number"
                          min="0"
                          value={formData.quotas.maxUsers}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxUsers: e.target.value }
                          })}
                          placeholder="Máximo de usuários (0 = ilimitado)"
                          className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="maxContacts" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Contatos
                        </label>
                        <input
                          id="maxContacts"
                          type="number"
                          min="0"
                          value={formData.quotas.maxContacts}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxContacts: e.target.value }
                          })}
                          placeholder="Máximo de contatos (0 = ilimitado)"
                          className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="maxCampaigns" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Campanhas
                        </label>
                        <input
                          id="maxCampaigns"
                          type="number"
                          min="0"
                          value={formData.quotas.maxCampaigns}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxCampaigns: e.target.value }
                          })}
                          placeholder="Máximo de campanhas (0 = ilimitado)"
                          className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="maxConnections" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Conexões
                        </label>
                        <input
                          id="maxConnections"
                          type="number"
                          min="0"
                          value={formData.quotas.maxConnections}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxConnections: e.target.value }
                          })}
                          placeholder="Máximo de conexões (0 = ilimitado)"
                          className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="maxMessagesPerMonth" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Mensagens/mês
                        </label>
                        <input
                          id="maxMessagesPerMonth"
                          type="number"
                          min="0"
                          value={formData.quotas.maxMessagesPerMonth}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, maxMessagesPerMonth: e.target.value }
                          })}
                          placeholder="Máximo de mensagens por mês (0 = ilimitado)"
                          className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="billingCycleDay" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Dia do ciclo
                        </label>
                        <input
                          id="billingCycleDay"
                          type="number"
                          min="1"
                          max="28"
                          value={formData.quotas.billingCycleDay}
                          onChange={(e) => setFormData({
                            ...formData,
                            quotas: { ...formData.quotas, billingCycleDay: e.target.value }
                          })}
                          placeholder="Dia da renovação das cotas (1-28)"
                          className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Providers permitidos - coluna única abaixo */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Provedores WhatsApp permitidos</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Selecione quais APIs de WhatsApp esta empresa pode utilizar.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(PROVIDER_INFO).map(([key, info]) => {
                    const isSelected = formData.allowedProviders.includes(key);
                    return (
                      <div
                        key={key}
                        onClick={() => handleProviderToggle(key)}
                        className={`
                          cursor-pointer rounded-xl border p-3 transition-all duration-200
                          ${isSelected
                            ? 'border-emerald-500 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/15'
                            : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/30 hover:border-gray-300 dark:hover:border-gray-600'
                          }
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                          />
                          <img
                            src={info.logo}
                            alt={info.name}
                            className="w-6 h-6 object-contain"
                          />
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm text-gray-800 dark:text-white/90">
                              {info.name}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {info.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Botões de ação */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/superadmin/empresas')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                      Salvando...
                    </>
                  ) : isNew ? (
                    <>
                      <i className="fas fa-plus text-base shrink-0" aria-hidden />
                      Criar empresa
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check text-base shrink-0" aria-hidden />
                      Salvar alterações
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </PageLayout>
    </>
  );
}
